var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useCallback, useMemo, useState } from "react";
import { TableHeader } from "./TableHeader";
import { TableRow } from "./TableRow";
import PropTypes from "prop-types";
import classNames from "classnames";
import Pagination from "../Pagination/Pagination";
import getColumns from "./Selection";
export var Table = function (_a) {
    var data = _a.data, columns = _a.columns, onRowClick = _a.onRowClick, onHeaderClick = _a.onHeaderClick, expandable = _a.expandable, singleExpandable = _a.singleExpandable, onExpand = _a.onExpand, _b = _a.loading, loading = _b === void 0 ? false : _b, emptyText = _a.emptyText, maxWidth = _a.maxWidth, maxHeight = _a.maxHeight, rowClassName = _a.rowClassName, _c = _a.withCloseExpandableIcon, withCloseExpandableIcon = _c === void 0 ? false : _c, pagination = _a.pagination, rowSelection = _a.rowSelection, _d = _a.rowKey, rowKey = _d === void 0 ? "key" : _d;
    var tableWrapperClasses = classNames({
        "mex-table__wrapper--scroll-x": !!maxWidth,
        "mex-table__wrapper--scroll-y": !!maxHeight,
        "mex-table__wrapper": true,
    });
    var _e = useState((pagination === null || pagination === void 0 ? void 0 : pagination.current) || 1), page = _e[0], setPage = _e[1];
    var getData = function () {
        if (pagination) {
            var _a = pagination.pageSize, pageSize = _a === void 0 ? 10 : _a;
            return data.slice((page - 1) * pageSize, page * pageSize);
        }
        return data;
    };
    var onPageChange = function (page) {
        var _a;
        (_a = pagination === null || pagination === void 0 ? void 0 : pagination.onChange) === null || _a === void 0 ? void 0 : _a.call(pagination, page);
        setPage(page);
    };
    var spreadPageProps = function (pagination) {
        return __assign(__assign({}, pagination), { onChange: onPageChange });
    };
    var getRowKey = useMemo(function () {
        if (typeof rowKey === "function")
            return rowKey;
        if (typeof rowKey === "string")
            return function (record) { return record[rowKey]; };
        return function (record) { return record.key; };
    }, [rowKey]);
    var generateColumns = useCallback(function () { return getColumns(rowSelection, data, getRowKey, columns); }, [rowSelection, data, getRowKey, columns]);
    var manipulatedColumns = generateColumns();
    return (_jsx(React.Fragment, { children: _jsxs("div", __assign({ className: tableWrapperClasses, style: { maxWidth: maxWidth, maxHeight: maxHeight } }, { children: [loading && (_jsxs("div", __assign({ className: "mex-table__loading" }, { children: [_jsx("i", { className: "mex-loader" }), typeof loading === "string" && _jsx("div", __assign({ className: "mex-table__loading-text" }, { children: loading }))] }))), _jsxs("table", __assign({ className: "mex-table" }, { children: [_jsx(TableHeader, { columns: manipulatedColumns, onHeaderClick: onHeaderClick }), _jsx("tbody", { children: !data.length ? (_jsx("tr", { children: _jsx("td", __assign({ colSpan: manipulatedColumns.length, className: "mex-table__no-data" }, { children: emptyText || "No data" })) })) : (_jsx(TableRow, { data: getData(), columns: manipulatedColumns, onRowClick: onRowClick, expandable: expandable, singleExpandable: singleExpandable, onExpand: onExpand, rowClassName: rowClassName, withCloseExpandableIcon: withCloseExpandableIcon })) })] })), pagination && _jsx(Pagination, __assign({}, spreadPageProps(pagination)))] })) }));
};
Table.propTypes = {
    data: PropTypes.array.isRequired,
    columns: PropTypes.arrayOf(PropTypes.shape({
        key: PropTypes.string.isRequired,
        title: PropTypes.oneOfType([PropTypes.string, PropTypes.node]).isRequired,
        dataIndex: PropTypes.string.isRequired,
        width: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    })).isRequired,
    onRowClick: PropTypes.func,
    onHeaderClick: PropTypes.func,
    onExpand: PropTypes.func,
    expandable: PropTypes.shape({
        expand: PropTypes.bool,
        content: PropTypes.func,
    }),
    singleExpandable: PropTypes.bool,
    loading: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
    emptyText: PropTypes.oneOfType([PropTypes.node, PropTypes.string]),
    maxWidth: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    maxHeight: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    rowClassName: PropTypes.func,
    withCloseExpandableIcon: PropTypes.bool,
    pagination: PropTypes.shape({
        pageSize: PropTypes.number,
        showTotal: PropTypes.func,
        total: PropTypes.number,
        current: PropTypes.number,
        onChange: PropTypes.func,
    }),
    rowSelection: PropTypes.shape({
        selectedRowKeys: PropTypes.arrayOf(PropTypes.string),
        onChange: PropTypes.func,
        getCheckboxProps: PropTypes.func,
    }),
    rowKey: PropTypes.func,
};
Table.defaultProps = {
    loading: false,
    withCloseExpandableIcon: false,
};
export default Table;
